import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

interface ReturnToBoardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    boardName: string;
}

export const ReturnToBoardModal: React.FC<ReturnToBoardModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    boardName
}) => {
    const { t } = useTranslation('common');
    const [timeLeft, setTimeLeft] = useState(10);

    // Reset timer when modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeLeft(10);
        }
    }, [isOpen]);

    // Timer logic
    useEffect(() => {
        if (!isOpen) return;

        if (timeLeft === 0) {
            onConfirm();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [isOpen, timeLeft, onConfirm]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white dark:bg-neutral-900 shadow-xl transition-all p-8 text-center border border-transparent dark:border-neutral-800">
                                 <div className="w-16 h-16 bg-accent-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                                     {/* Icon: Arrow taking a U-turn or similar. Using a simple Back/Return icon */}
                                     <span className="w-8 h-8 text-accent-primary" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_210db97d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_210db97d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                 </div>

                                 <Dialog.Title
                                     as="h3"
                                     className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2"
                                 >
                                     {t('boards.returnToBoard', 'Return to Board')}
                                 </Dialog.Title>

                                 <div className="mt-2">
                                     <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-8 leading-relaxed font-light">
                                         {t('boards.returnToBoardConfirm', { boardName })}
                                     </p>
                                 </div>

                                 <div className="space-y-3">
                                     <button
                                         onClick={onConfirm}
                                         className="w-full h-11 bg-accent-primary text-white font-bold rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"
                                     >
                                         {t('boards.returnNow', 'Return Now')}
                                     </button>

                                     <button
                                         onClick={onClose}
                                         className="w-full h-11 bg-transparent text-neutral-500 dark:text-neutral-400 font-medium rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all"
                                     >
                                         {t('actions.cancel')} ({timeLeft}s)
                                     </button>
                                 </div>
                            </Dialog.Panel>

                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
